'use client';
import * as ToggleGroupPrimitive from '@radix-ui/react-toggle-group';
import { cn } from '@utils/cn';
import { VariantProps } from 'class-variance-authority';
import * as React from 'react';
import { toggleVariants } from './toggle';
import { Wobble } from './wobble';
import { ClientOnly } from '@headless/ClientOnly';

const ToggleGroupContext = React.createContext<VariantProps<typeof toggleVariants>>({
  size: 'default',
  variant: 'default',
});

type ToggleGroupProps = React.ComponentPropsWithoutRef<typeof ToggleGroupPrimitive.Root> &
  VariantProps<typeof toggleVariants>;

const ToggleGroup = React.forwardRef<
  React.ComponentRef<typeof ToggleGroupPrimitive.Root>,
  ToggleGroupProps
>(({ className, variant, size, children, ...props }, ref) => {
  // For some reason, the ToggleGroup runs on the server but then disappears completely on the client with some hydration errors
  // Wrapping it in a ClientOnly fixes this
  return (
    <ClientOnly>
      <ToggleGroupPrimitive.Root
        ref={ref}
        className={cn(
          'flex items-center justify-center gap-1',
          size === 'card' && 'grid grid-cols-2 items-stretch gap-2',
          className,
        )}
        {...props}
      >
        <ToggleGroupContext.Provider value={{ variant, size }}>
          {children}
        </ToggleGroupContext.Provider>
      </ToggleGroupPrimitive.Root>
    </ClientOnly>
  );
});

ToggleGroup.displayName = ToggleGroupPrimitive.Root.displayName;

const ToggleGroupItem = React.forwardRef<
  React.ComponentRef<typeof ToggleGroupPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof ToggleGroupPrimitive.Item> &
    VariantProps<typeof toggleVariants>
>(({ className, children, variant, size, ...props }, ref) => {
  const context = React.useContext(ToggleGroupContext);

  return (
    <Wobble ref={ref}>
      <ToggleGroupPrimitive.Item
        ref={ref}
        className={cn(
          toggleVariants({
            variant: context.variant || variant,
            size: context.size || size,
          }),
          className,
        )}
        {...props}
      >
        {children}
      </ToggleGroupPrimitive.Item>
    </Wobble>
  );
});

ToggleGroupItem.displayName = ToggleGroupPrimitive.Item.displayName;

export { ToggleGroup, ToggleGroupItem, ToggleGroupPrimitive, type ToggleGroupProps };
